import { usePersistedFilters } from '~/hooks/usePersistedFilters'
import {
  type SpotPriceFilters,
  spotPriceFiltersCodec,
} from '~/pages/SpotPrice/models/persistedFilters'

/**
 * スポット価格のフィルタバー保持 key。
 *
 * AC-SC-031 [P0] (`docs/specs/common/shared-components.md`) は「フィルタバーを持つ
 * 全画面において、フィルタの選択状態を localStorage で永続化し、他画面への遷移後・
 * 再訪時に前回の選択状態を復元する」と保存媒体まで規定するため localStorage を使う
 * (タブ生存期間で消える sessionStorage は「再訪時に復元」を満たさない)。
 */
export const SPOT_PRICE_FILTERS_STORAGE_KEY = 'ebalance-spot-price-filters'

type UsePersistedSpotPriceFiltersResult = {
  dateFrom: string
  setDateFrom: (value: string) => void
  dateTo: string
  setDateTo: (value: string) => void
  visibleAreas: Set<string>
  setVisibleAreas: (updater: (prev: Set<string>) => Set<string>) => void
}

/**
 * 期間 (開始日 / 終了日) と表示系列を localStorage に保持し、供給力変化画面への
 * 遷移や再訪をまたいで復元する hook (AC-SC-031)。
 *
 * 直列化・復元・既定値・検証は `models/persistedFilters.ts` の純関数が SoT。
 * 読み書きのタイミング (初期復元 / 変更時の書き戻し / 未操作の既定値は書かない /
 * Strict Mode 対策) は共通 hook が持つ。
 */
export function usePersistedSpotPriceFilters(): UsePersistedSpotPriceFiltersResult {
  const { filters, updateFilters } = usePersistedFilters<SpotPriceFilters>({
    storageKey: SPOT_PRICE_FILTERS_STORAGE_KEY,
    codec: spotPriceFiltersCodec,
  })

  return {
    dateFrom: filters.dateFrom,
    setDateFrom: (value) =>
      updateFilters((prev) => ({ ...prev, dateFrom: value })),
    dateTo: filters.dateTo,
    setDateTo: (value) => updateFilters((prev) => ({ ...prev, dateTo: value })),
    visibleAreas: filters.visibleAreas,
    // 系列トグルは前回値から次を作る形なので updater をそのまま通す
    // (呼び出し側が `new Set(prev)` を作って add/delete する既存の書き方を保つ)。
    setVisibleAreas: (updater) =>
      updateFilters((prev) => ({
        ...prev,
        visibleAreas: updater(prev.visibleAreas),
      })),
  }
}
